import { createFileRoute, getRouteApi } from '@tanstack/react-router'
import { z } from 'zod'

import { CampaignDetailPage, type CampaignView } from '@/features/campaigns'
// Through `queries`, not the feature index: a loader is eager by construction.
import {
  campaignFiltersQuery,
  campaignQuery,
  campaignSourcesQuery,
} from '@/features/campaigns/queries'
import { sliceFromSearch, slicePatchToSearch } from '@/features/leads'
import { leadsQueryFor } from '@/features/leads/queries'


// Inlined rather than imported from the feature: `validateSearch` runs eagerly,
// so importing a runtime value from the feature barrel here would pin the whole
// (otherwise lazy) detail page into the initial bundle. The type still comes
// from the feature and keeps this list honest.
const CAMPAIGN_VIEWS = ['settings', 'leads'] as const satisfies readonly CampaignView[]

// Links minted against the layouts this replaced still resolve. `stats` lands
// on the leads view rather than on the settings form: that is where the numbers
// went, so a link to the report still arrives at the report.
const LEGACY_TABS = ['overview', 'sources', 'filters', 'stats'] as const

// The slice the leads view is showing. Inlined for the same reason as the views
// above, and checked against the feature's own type where it is read.
//
// `period` drives both halves of that view — the counters and the table below
// them read one window, so «посмотри на эти цифры» arrives at the same cut as
// the rows they came from.
const sliceSchema = {
  period: z.enum(['24h', '7d', '30d']).default('7d'),
  q: z.string().default(''),
  type: z.string().default(''),
  served: z.string().default(''),
  country: z.string().default(''),
  source: z.string().default(''),
}

const searchSchema = z.object({
  view: z
    .enum([...CAMPAIGN_VIEWS, ...LEGACY_TABS])
    .default('settings')
    .transform((value): CampaignView =>
      value === 'leads' || value === 'stats' ? 'leads' : 'settings',
    ),
  ...sliceSchema,
  // The leads view keeps its own paging and selection in the URL.
  page: z.number().int().min(0).default(0),
  // Rows per page rides in the URL too, so a link and a reload both land on the
  // density the reader chose. Closed set: it reaches the backend as `limit`.
  pageSize: z.union([z.literal(25), z.literal(50), z.literal(100)]).default(50),
  leadId: z.string().optional(),
})

export const Route = createFileRoute('/_auth/campaigns/$campaignId')({
  validateSearch: searchSchema,
  // `leadId` is absent on purpose: it opens the detail sheet and changes
  // nothing about which rows the table asks for.
  loaderDeps: ({ search }) => ({
    view: search.view,
    period: search.period,
    q: search.q,
    type: search.type,
    served: search.served,
    country: search.country,
    source: search.source,
    page: search.page,
    pageSize: search.pageSize,
  }),
  /*
   * The campaign itself, and — on the settings view — the two collections that
   * form is made of. `CampaignForm` used to fetch its sources and filters on
   * mount and draw three fixed-height skeletons in the meantime, which measured
   * as a 923px stage for 57ms between the 266px list and the 1441px page. Those
   * skeletons cannot be the right height: how many sources and filters a
   * campaign has is what they are waiting to find out.
   *
   * The report behind the leads view and the breakdowns under it stay on the
   * component with their own reserved space — critical data awaited, the rest
   * deferred, which is the split the router's own docs recommend.
   */
  loader: async ({ context, params, deps }) => {
    await context.queryClient.ensureQueryData(campaignQuery(params.campaignId))

    if (deps.view === 'settings') {
      await Promise.all([
        context.queryClient.ensureQueryData(campaignSourcesQuery(params.campaignId)),
        context.queryClient.ensureQueryData(campaignFiltersQuery(params.campaignId)),
      ])
      return
    }

    // The leads view. `leadsQueryFor` is the same builder the table calls, so
    // the key preloaded here is the key it asks for. The report and its
    // breakdowns are not awaited: `CampaignTotals` reserves their measured
    // heights, so they can arrive late without moving anything.
    await context.queryClient.ensureQueryData(
      leadsQueryFor(sliceFromSearch(deps), {
        campaignId: params.campaignId,
        page: deps.page,
        pageSize: deps.pageSize,
      }),
    )
  },
  component: CampaignDetailRoute,
})

const routeApi = getRouteApi('/_auth/campaigns/$campaignId')

function CampaignDetailRoute() {
  const { campaignId } = routeApi.useParams()
  const { view, page, leadId, pageSize, ...slice } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  return (
    <CampaignDetailPage
      campaignId={campaignId}
      view={view}
      page={page}
      pageSize={pageSize}
      leadId={leadId}
      slice={sliceFromSearch(slice)}
      onViewChange={(next) => void navigate({ search: (prev) => ({ ...prev, view: next }) })}
      // Narrowing the slice always returns to the first page: page 7 of the old
      // selection is a different set of rows, and often no rows at all.
      onSliceChange={(patch) =>
        void navigate({ search: (prev) => ({ ...prev, ...slicePatchToSearch(patch), page: 0 }) })
      }
      onPageChange={(next) => void navigate({ search: (prev) => ({ ...prev, page: next }) })}
      // A new page size makes the current page number meaningless — page 7 of
      // fifties is not page 7 of hundreds.
      onPageSizeChange={(next) =>
        void navigate({ search: (prev) => ({ ...prev, pageSize: next as 25 | 50 | 100, page: 0 }) })
      }
      onSelectLead={(next) => void navigate({ search: (prev) => ({ ...prev, leadId: next }) })}
    />
  )
}
